import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const METRIKY_URL = "leftmenu/kim"
const INDIKATORY_URL = "indikatorinfo"
const METRIKA_GRAF_URL = "kim_graf/"

export const useKimStore = defineStore('kim', () => {
    const mainStore = useMainStore()

    const indikatory = ref<IndikatorItem[]>([])
    const metrikyTree = ref<KimKmecMenuItem[]>([])
    const metrikyChartData = ref<Record<string, Record<string, number>>>({})
    const selectedSkupinaMetrik = ref<Skupinametrik | undefined>(undefined)
    const selectedSkupinaMetrikKod = ref<string | undefined>("")
    const openIndikator = ref<string>("")
    const selectedIndikator = ref<IndikatorItem | null>(null)
    const loadingTree = ref<boolean>(false)
    const loadingMetrikaFromUrl = ref<boolean>(false)
    const notFound = ref<boolean>(false)
    const searchInput = ref<string>("")
    const searchSelect = ref<"metriky" | "indikatory">("indikatory")
    const metrikyFilter = ref<"all" | "indikatory" | "indikatoryUR" | "metrikyMPP" | "metrikyAZUR">("all")
    const hasChartData = ref<boolean>(false)
    const noData = ref<boolean>(false)

    const filteredMetrikyTree = computed(() => {
        const oblasti: KimKmecMenuItem[] = []
        metrikyTree.value.forEach(oblast => {
            const celky: Celek[] = []
            const newOblast = { ...oblast }
            oblast.CELEK.forEach(celek => {
                const skupiny: Skupinametrik[] = []
                const newCelek = { ...celek }
                celek.SKUPINAMETRIK.forEach(skupina => {
                    const metriky: Metrika[] = []
                    const newSkupina = { ...skupina }
                    skupina.METRIKA.forEach(metrika => {
                        if (
                            metrikyFilter.value === "all" ||
                            (metrikyFilter.value === "indikatory" && metrika.KI_INDIKATOR_KOD !== null) ||
                            (metrikyFilter.value === "indikatoryUR" && metrika.KI_CIL_UR_KOD !== null) ||
                            (metrikyFilter.value === "metrikyMPP" && metrika.M_MPP === 1) ||
                            (metrikyFilter.value === "metrikyAZUR" && metrika.M_AZUR === 1)
                        ) {
                            metriky.push(metrika)
                        }
                    })
                    if (metriky.length > 0) {
                        newSkupina.METRIKA = metriky
                        if (searchInput.value.length > 2) {
                            if (
                                skupina.NAZEV.toLowerCase().includes(searchInput.value.toLowerCase()) ||
                                skupina.KOD.toLowerCase().includes(searchInput.value.toLowerCase())
                            ) {
                                skupiny.push(newSkupina)
                            }
                        } else {
                            skupiny.push(newSkupina)
                        }
                    }
                })
                if (skupiny.length > 0) {
                    newCelek.SKUPINAMETRIK = skupiny
                    celky.push(newCelek)
                }
            })
            if (celky.length > 0) {
                newOblast.CELEK = celky
                oblasti.push(newOblast)
            }
        })
        return oblasti
    })

    const fetchMetrikyAndIndikatory = async () => {
        
        loadingTree.value = true
        const data = await Promise.all([
            $fetch<KimKmecMenuItem[]>(METRIKY_URL, {
                baseURL: import.meta.client ? mainStore.apiBaseUrl : mainStore.apiBaseUrlInt,
            }),
            $fetch<IndikatorItem[]>(INDIKATORY_URL, {
                baseURL: import.meta.client ? mainStore.apiBaseUrl : mainStore.apiBaseUrlInt,
            }),
        ])
        if (data) {
            metrikyTree.value = data[0]
            indikatory.value = data[1]
            loadingTree.value = false
        }
        return [metrikyTree.value, indikatory.value]
    }

    const setSelectedSkupinaMetrik = async (kod: string) => {
        selectedIndikator.value = null
        selectedSkupinaMetrikKod.value = kod
        if (selectedSkupinaMetrikKod.value.includes("m")) {
            let kod = selectedSkupinaMetrikKod.value
            if (kod.length > 14) {
                kod = kod.substring(0, kod.lastIndexOf("."))
            }
            selectedSkupinaMetrik.value = await findSkupinaMetrik(kod)
        } else if (selectedSkupinaMetrikKod.value.includes("i")) {
            const indikator = indikatory.value.find(item => item.KOD === selectedSkupinaMetrikKod.value)
            if (!indikator) {
                notFound.value = true
                searchSelect.value = "indikatory"
            } else {
                const kod = (indikator.METRIKA_KOD as string).substring(0, (indikator.METRIKA_KOD as string).lastIndexOf("."))
                selectedSkupinaMetrik.value = await findSkupinaMetrik(kod)
                openIndikator.value = selectedSkupinaMetrikKod.value
            }
        }
        if (selectedSkupinaMetrik.value) {
            try {
                const metrikyIds = selectedSkupinaMetrik.value.METRIKA.map(item => item.M_ID)
                await fetchMetrikyChartData(metrikyIds)
            } catch (error) {
                console.log(error)
            }
        }
    }

    const fetchMetrikyChartData = async (metrikaIds: number[]) => {
        loadingMetrikaFromUrl.value = true
        try {
            const promises = metrikaIds.map(id => {
                metrikyChartData.value[id.toString()] = {}
                return $fetch<MetrikaGrafResponse>(METRIKA_GRAF_URL + id, {
                    baseURL: import.meta.client ? mainStore.apiBaseUrl : mainStore.apiBaseUrlInt,
                })
            })
            const data = await Promise.all(promises)
            if (data) {
                data.forEach((metrikaData, index) => {
                    metrikyChartData.value[(metrikaIds[index] as number).toString()] = metrikaData
                })
                hasChartData.value = true
                noData.value = false
            }
            return data
        } catch (error) {
            console.log(error)
        } finally {
            loadingMetrikaFromUrl.value = false
        }
    }

    const findSkupinaMetrik = async (kod: string): Promise<Skupinametrik | undefined> => {
        const oblastKod = kod.split(".")[1]
        const celekKod = oblastKod + "." + kod.split(".")[2]
        if (metrikyTree.value.length === 0) {
            console.warn("Seznam metrik nebyl načten!")
            return
        }
        const oblast = metrikyTree.value.find(oblast => oblast.KOD === oblastKod)
        if (!oblast) {
            notFound.value = true
            searchSelect.value = "metriky"
            return
        }
        const celek = oblast.CELEK.find(celek => celek.KOD === celekKod)
        if (!celek) {
            notFound.value = true
            searchSelect.value = "metriky"
            return
        }
        const metrika = celek.SKUPINAMETRIK.find(skupina => skupina.KOD === kod)
        if (!metrika) {
            notFound.value = true
            searchSelect.value = "metriky"
            return
        }
        notFound.value = false
        return metrika
    }

    return {
        indikatory,
        metrikyTree,
        metrikyChartData,
        selectedSkupinaMetrik,
        selectedSkupinaMetrikKod,
        openIndikator,
        selectedIndikator,
        loadingTree,
        loadingMetrikaFromUrl,
        notFound,
        searchInput,
        searchSelect,
        metrikyFilter,
        hasChartData,
        noData,
        filteredMetrikyTree,
        fetchMetrikyAndIndikatory,
        setSelectedSkupinaMetrik,
        fetchMetrikyChartData,
        findSkupinaMetrik
    }
})

if ((import.meta as MetaHot).hot) {
    (import.meta as MetaHot).hot?.accept(acceptHMRUpdate(useKimStore, (import.meta as MetaHot).hot))
}

/* const METRIKY_URL = "leftmenu/kim"

const INDIKATORY_URL = "indikatorinfo"

const METRIKA_GRAF_URL = "kim_graf/"

type KimStore = {
    indikatory: IndikatorItem[]
    metrikyTree: KimKmecMenuItem[]
    metrikyChartData: Record<string, Record<string, number>>
    selectedSkupinaMetrik: Skupinametrik | undefined
    selectedSkupinaMetrikKod?: string
    openIndikator: string
    selectedIndikator: IndikatorItem | null
    loadingTree: boolean
    loadingMetrikaFromUrl: boolean
    notFound: boolean
    searchInput: string
    searchSelect: "metriky" | "indikatory"
    metrikyFilter:
        | "all"
        | "indikatory"
        | "indikatoryUR"
        | "metrikyMPP"
        | "metrikyAZUR"
    hasChartData: boolean
    noData: boolean
}

const useKimStore = defineStore("kim", {
    state: (): KimStore => ({
        indikatory: [],
        metrikyTree: [],
        metrikyChartData: {},
        selectedSkupinaMetrik: undefined,
        selectedSkupinaMetrikKod: "",
        openIndikator: "",
        selectedIndikator: null,
        loadingTree: false,
        loadingMetrikaFromUrl: false,
        notFound: false,
        searchInput: "",
        searchSelect: "indikatory",
        metrikyFilter: "all",
        hasChartData: false,
        noData: false,
    }),
    getters: {
        filteredMetrikyTree() {
            const oblasti: KimKmecMenuItem[] = []
            this.metrikyTree.forEach(oblast => {
                const celky: Celek[] = []
                const newOblast = { ...oblast }
                oblast.CELEK.forEach(celek => {
                    const skupiny: Skupinametrik[] = []
                    const newCelek = { ...celek }
                    celek.SKUPINAMETRIK.forEach(skupina => {
                        const metriky: Metrika[] = []
                        const newSkupina = { ...skupina }
                        skupina.METRIKA.forEach(metrika => {
                            if (
                                this.metrikyFilter === "all" ||
                                (this.metrikyFilter === "indikatory" &&
                                    metrika.KI_INDIKATOR_KOD !== null) ||
                                (this.metrikyFilter === "indikatoryUR" &&
                                    metrika.KI_CIL_UR_KOD !== null) ||
                                (this.metrikyFilter === "metrikyMPP" &&
                                    metrika.M_MPP === 1) ||
                                (this.metrikyFilter === "metrikyAZUR" &&
                                    metrika.M_AZUR === 1)
                            ) {
                                metriky.push(metrika)
                            }
                        })
                        if (metriky.length > 0) {
                            newSkupina.METRIKA = metriky
                            if (this.searchInput.length > 2) {
                                if (
                                    skupina.NAZEV.toLowerCase().includes(
                                        this.searchInput.toLowerCase()
                                    ) ||
                                    skupina.KOD.toLowerCase().includes(
                                        this.searchInput.toLowerCase()
                                    )
                                ) {
                                    skupiny.push(newSkupina)
                                }
                            } else {
                                skupiny.push(newSkupina)
                            }
                        }
                    })
                    if (skupiny.length > 0) {
                        newCelek.SKUPINAMETRIK = skupiny
                        celky.push(newCelek)
                    }
                })
                if (celky.length > 0) {
                    newOblast.CELEK = celky
                    oblasti.push(newOblast)
                }
            })
            return oblasti
        },
    },
    actions: {
        async fetchMetrikyAndIndikatory() {
            const mainStore = useMainStore()
            this.loadingTree = true
            const data = await Promise.all([
                $fetch<KimKmecMenuItem[]>(METRIKY_URL, {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                }),
                $fetch<IndikatorItem[]>(INDIKATORY_URL, {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                }),
            ])
            if (data) {
                this.metrikyTree = data[0]
                this.indikatory = data[1]

                this.loadingTree = false
            }
            return [this.metrikyTree, this.indikatory]
        },
        async setSelectedSkupinaMetrik(kod: string) {
            this.selectedIndikator = null
            this.selectedSkupinaMetrikKod = kod
            if (this.selectedSkupinaMetrikKod.includes("m")) {
                let kod = this.selectedSkupinaMetrikKod
                // check if kod is skupina metrik or metrika
                if (kod.length > 14) {
                    kod = kod.substring(0, kod.lastIndexOf("."))
                }
                this.selectedSkupinaMetrik = await this.findSkupinaMetrik(kod)
            } else if (this.selectedSkupinaMetrikKod.includes("i")) {
                const indikator = this.indikatory.find(item => {
                    return item.KOD === this.selectedSkupinaMetrikKod
                })
                if (!indikator) {
                    this.notFound = true
                    this.searchSelect = "indikatory"
                } else {
                    const kod = (indikator.METRIKA_KOD as string).substring(
                        0,
                        (indikator.METRIKA_KOD as string).lastIndexOf(".")
                    )
                    this.selectedSkupinaMetrik =
                        await this.findSkupinaMetrik(kod)
                    this.openIndikator = this.selectedSkupinaMetrikKod
                }
            }
            if (this.selectedSkupinaMetrik) {
                try {
                    const metrikyIds = this.selectedSkupinaMetrik.METRIKA.map(
                        item => item.M_ID
                    )
                    await this.fetchMetrikyChartData(metrikyIds)
                } catch (error) {
                    console.log(error)
                }
            }
        },
        async fetchMetrikyChartData(metrikaIds: number[]) {
            const mainStore = useMainStore()
            this.loadingMetrikaFromUrl = true
            try {
                // this.selectedSkupinaMetrikKod as string
                const promises = metrikaIds.map(id => {
                    this.metrikyChartData[id.toString()] = {}

                    return $fetch<MetrikaGrafResponse>(METRIKA_GRAF_URL + id, {
                        baseURL: import.meta.client
                            ? mainStore.apiBaseUrl
                            : mainStore.apiBaseUrlInt,
                    })
                })
                const data = await Promise.all(promises)
                if (data) {
                    data.forEach((metrikaData, index) => {
                        this.metrikyChartData[
                            (metrikaIds[index] as number).toString()
                        ] = metrikaData
                    })
                    this.hasChartData = true
                    this.noData = false
                }
                return data
            } catch (error) {
                console.log(error)
            } finally {
                this.loadingMetrikaFromUrl = false
            }
        },
        async findSkupinaMetrik(
            kod: string
        ): Promise<Skupinametrik | undefined> {
            const oblastKod = kod.split(".")[1]
            const celekKod = oblastKod + "." + kod.split(".")[2]
            if (this.metrikyTree.length === 0) {
                console.warn("Seznam metrik nebyl načten!")
                return
            }
            const oblast = this.metrikyTree.find(oblast => {
                return oblast.KOD === oblastKod
            })
            if (!oblast) {
                this.notFound = true
                this.searchSelect = "metriky"
                return
            }
            const celek = oblast.CELEK.find(celek => {
                return celek.KOD === celekKod
            })
            if (!celek) {
                this.notFound = true
                this.searchSelect = "metriky"
                return
            }
            const metrika = celek.SKUPINAMETRIK.find(skupina => {
                return skupina.KOD === kod
            })
            if (!metrika) {
                this.notFound = true
                this.searchSelect = "metriky"
                return
            }
            this.notFound = false
            return metrika
        },
    },
})

if ((import.meta as MetaHot).hot) {
    ;(import.meta as MetaHot).hot?.accept(
        acceptHMRUpdate(useKimStore, (import.meta as MetaHot).hot)
    )
}

export { useKimStore }
 */